/* ============================================================
   MM-WISHLIST - Mae & Mini anonymous wishlist
   localStorage-backed list + Klaviyo profile sync + optional
   cross-device server sync (via dashboard /api/wishlist) when
   customer is logged in. Public API on window.MMWishlist.
   ============================================================ */
(function () {
  'use strict';

  const STORAGE_KEY = 'mm_wishlist_v2';
  const EMAIL_KEY = 'mm_wishlist_email';
  const SUBSCRIBED_KEY = 'mm_wishlist_subscribed';
  const REMOVED_KEY = 'mm_wishlist_removed_v1';
  const TOMBSTONE_TTL = 90 * 24 * 60 * 60 * 1000; // prune removals older than 90 days
  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_BASE = 'https://a.klaviyo.com/client';

  const cfg = (window.MMWishlistConfig || {});
  const KLAVIYO_KEY = cfg.klaviyoPublicKey || '';
  const KLAVIYO_LIST_ID = cfg.klaviyoListId || '';
  const CUSTOMER_ID = cfg.customerId || null;
  const CUSTOMER_EMAIL = cfg.customerEmail || null;
  const SYNC = cfg.serverSync || { enabled: false };

  const state = {
    items: [],
    ready: false,
    email: null,
    subscribed: false,
    serverLoaded: false,
  };

  const listeners = [];
  let syncDebounceTimer = null;
  let profileDebounceTimer = null;

  function emit(event, payload) {
    listeners.forEach(function (l) {
      try { l(event, payload); } catch (e) { /* swallow */ }
    });
  }

  function on(callback) {
    listeners.push(callback);
    if (state.ready) callback('ready', { items: state.items.slice() });
  }

  function track(eventName, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, params || {});
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: eventName }, params || {}));
      }
      if (typeof window.clarity === 'function') window.clarity('event', eventName);
    } catch (e) { /* swallow */ }
  }

  // ----- localStorage -----
  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }

  function write(items) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); return true; }
    catch (e) { console.warn('[mm-wishlist] write failed', e); return false; }
  }

  // ----- Removal tombstones -----
  // Deletions must survive the cross-device union merge: without a "removed at"
  // record, any other device (or the server copy) re-introduces the item on the
  // next merge. Tombstones travel to the server as pseudo-items ({_removed:true})
  // inside the same items array, so the storage backend needs no schema change.
  function itemKey(productId, variantId) {
    return String(productId) + ':' + String(variantId || '');
  }

  function readTombstones() {
    try {
      const raw = localStorage.getItem(REMOVED_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const now = Date.now();
      const pruned = {};
      Object.keys(parsed).forEach(function (k) {
        const ts = parsed[k];
        if (typeof ts === 'number' && now - ts < TOMBSTONE_TTL) pruned[k] = ts;
      });
      return pruned;
    } catch (e) { return {}; }
  }

  function writeTombstones() {
    try { localStorage.setItem(REMOVED_KEY, JSON.stringify(tombstones)); } catch (e) { /* swallow */ }
  }

  let tombstones = readTombstones();

  function tombstoneItems() {
    return Object.keys(tombstones).map(function (k) {
      const sep = k.indexOf(':');
      return {
        productId: k.slice(0, sep),
        variantId: k.slice(sep + 1),
        _removed: true,
        removedAt: tombstones[k],
      };
    });
  }

  function readEmail() {
    try { return localStorage.getItem(EMAIL_KEY) || null; } catch (e) { return null; }
  }
  function writeEmail(email) {
    try { localStorage.setItem(EMAIL_KEY, email || ''); } catch (e) { /* swallow */ }
  }
  function readSubscribed() {
    try { return localStorage.getItem(SUBSCRIBED_KEY) === '1'; } catch (e) { return false; }
  }
  function writeSubscribed(flag) {
    try { localStorage.setItem(SUBSCRIBED_KEY, flag ? '1' : '0'); } catch (e) { /* swallow */ }
  }

  // Dedupe key: productId + variantId (so different variants of the same product
  // are stored as separate items and heart icons stay variant-specific)
  function findIndex(productId, variantId) {
    const pid = String(productId);
    const vid = String(variantId || '');
    for (let i = 0; i < state.items.length; i++) {
      const it = state.items[i];
      if (String(it.productId) === pid && String(it.variantId || '') === vid) return i;
    }
    return -1;
  }

  function normalize(productData) {
    const priceRaw = productData.price;
    let price = 0;
    if (typeof priceRaw === 'number') price = priceRaw;
    else if (priceRaw != null) price = parseInt(priceRaw, 10) || 0;
    return {
      productId: String(productData.productId || ''),
      variantId: productData.variantId ? String(productData.variantId) : '',
      variantTitle: productData.variantTitle || '',
      // sizePicked = true when user explicitly selected size (PDP or size picker)
      // false when size was auto-defaulted (collection card heart click)
      sizePicked: productData.sizePicked === true,
      handle: productData.handle || '',
      title: productData.title || '',
      imageUrl: productData.imageUrl || '',
      url: productData.url || (productData.handle ? '/products/' + productData.handle : ''),
      price: price,
      addedAt: productData.addedAt || Date.now(),
    };
  }

  function showToast(message) {
    let toast = document.querySelector('[data-mm-wishlist-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.setAttribute('data-mm-wishlist-toast', '');
      toast.style.cssText = 'position:fixed;bottom:1.5rem;left:50%;transform:translateX(-50%);background:#2C3E50;color:#fff;padding:0.875rem 1.25rem;border-radius:999px;font-family:DM Sans,sans-serif;font-size:0.875rem;font-weight:500;box-shadow:0 8px 24px rgba(0,0,0,0.18);z-index:9999;opacity:0;transition:opacity 0.2s ease;pointer-events:none;';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.opacity = '1';
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { toast.style.opacity = '0'; }, 2500);
  }

  // ----- Klaviyo client API -----
  function klaviyoFetch(path, body) {
    if (!KLAVIYO_KEY) return Promise.reject(new Error('No Klaviyo key'));
    return fetch(KLAVIYO_BASE + path + '?company_id=' + encodeURIComponent(KLAVIYO_KEY), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
      mode: 'cors',
    });
  }

  function profileProps() {
    const handles = state.items.map(function (i) { return i.handle; }).filter(Boolean);
    const titles = state.items.map(function (i) { return i.title; }).filter(Boolean);
    const totalValue = state.items.reduce(function (sum, i) { return sum + (i.price || 0); }, 0);
    return {
      wishlist_count: state.items.length,
      wishlist_handles: handles,
      wishlist_titles: titles,
      wishlist_total_value: totalValue / 100,
      wishlist_updated_at: new Date().toISOString(),
    };
  }

  function subscribeEmail(email, opts) {
    opts = opts || {};
    if (!email) return Promise.reject(new Error('No email'));
    if (!KLAVIYO_LIST_ID) {
      console.warn('[mm-wishlist] No Klaviyo list ID configured; skipping subscribe');
      // Still save locally so we can sync profile properties + auto re-engage later
      state.email = email;
      state.subscribed = true;
      writeEmail(email);
      writeSubscribed(true);
      emit('subscribed', { email: email, list: null });
      return Promise.resolve(null);
    }
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: opts.source || 'mm-wishlist',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: profileProps(),
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: KLAVIYO_LIST_ID } },
        },
      },
    };
    return klaviyoFetch('/subscriptions/', body).then(function (res) {
      if (!res.ok && res.status !== 202) {
        return res.text().then(function (t) { throw new Error('Klaviyo subscribe ' + res.status + ': ' + t); });
      }
      state.email = email;
      state.subscribed = true;
      writeEmail(email);
      writeSubscribed(true);
      track('mm_wishlist_subscribe', { email: email, list_id: KLAVIYO_LIST_ID });
      emit('subscribed', { email: email, list: KLAVIYO_LIST_ID });
      return res;
    });
  }

  function syncProfile(email) {
    email = email || state.email;
    if (!email || !KLAVIYO_KEY) return Promise.resolve(null);
    const body = {
      data: {
        type: 'profile',
        attributes: {
          email: email,
          properties: profileProps(),
        },
      },
    };
    return klaviyoFetch('/profiles/', body).catch(function (e) {
      console.warn('[mm-wishlist] profile sync failed', e);
    });
  }

  function trackEvent(eventName, item) {
    const email = state.email;
    if (!email || !KLAVIYO_KEY) return Promise.resolve(null);
    const properties = item ? {
      product_id: item.productId,
      product_handle: item.handle,
      product_title: item.title,
      product_url: item.url,
      product_image: item.imageUrl,
      product_price: (item.price || 0) / 100,
      wishlist_count: state.items.length,
    } : { wishlist_count: state.items.length };
    const body = {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: eventName } } },
          profile: { data: { type: 'profile', attributes: { email: email } } },
          properties: properties,
        },
      },
    };
    return klaviyoFetch('/events/', body).catch(function (e) {
      console.warn('[mm-wishlist] event track failed', e);
    });
  }

  function scheduleProfileSync() {
    if (!state.email) return;
    clearTimeout(profileDebounceTimer);
    profileDebounceTimer = setTimeout(function () { syncProfile(); }, 1500);
  }

  // ----- Server sync (dashboard /api/wishlist) -----
  function serverHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-MM-Customer-Id': String(CUSTOMER_ID || ''),
      'X-MM-Token': SYNC.token || '',
      'X-MM-Ts': SYNC.ts || '',
    };
  }

  function loadFromServer() {
    if (!SYNC.enabled || !SYNC.url || !CUSTOMER_ID) return Promise.resolve(null);
    return fetch(SYNC.url + '?customer_id=' + encodeURIComponent(CUSTOMER_ID), {
      method: 'GET',
      headers: serverHeaders(),
      mode: 'cors',
      credentials: 'omit',
    }).then(function (r) {
      if (!r.ok) throw new Error('server load ' + r.status);
      return r.json();
    }).then(function (data) {
      const rawItems = (data && Array.isArray(data.items)) ? data.items : [];
      const serverItems = rawItems.filter(function (it) { return it && !it._removed && it.productId; });
      const serverTombstones = rawItems.filter(function (it) { return it && it._removed === true && it.productId; });

      // 1. Merge server tombstones into the local set (newest removal wins)
      serverTombstones.forEach(function (t) {
        const k = itemKey(t.productId, t.variantId);
        const ts = typeof t.removedAt === 'number' ? t.removedAt : 0;
        if (!tombstones[k] || ts > tombstones[k]) tombstones[k] = ts;
      });

      // 2. Merge items by productId + variantId (newest addedAt wins), but a
      //    tombstone kills any copy whose addedAt is not newer than the removal.
      //    An item re-added AFTER the removal wins and clears the tombstone.
      const map = {};
      state.items.forEach(function (it) { map[itemKey(it.productId, it.variantId)] = it; });
      serverItems.forEach(function (it) {
        const norm = normalize(it);
        // A server item without addedAt must not look "just added" (normalize
        // stamps Date.now()), or it would beat and clear a legitimate tombstone.
        if (typeof it.addedAt !== 'number') norm.addedAt = 0;
        const k = itemKey(norm.productId, norm.variantId);
        if (!map[k] || (norm.addedAt || 0) > (map[k].addedAt || 0)) {
          map[k] = norm;
        }
      });
      Object.keys(map).forEach(function (k) {
        if (tombstones[k] == null) return;
        if ((map[k].addedAt || 0) > tombstones[k]) {
          delete tombstones[k];
        } else {
          delete map[k];
        }
      });
      writeTombstones();

      state.items = Object.keys(map).map(function (k) { return map[k]; });
      write(state.items);
      state.serverLoaded = true;
      emit('merged', { items: state.items.slice() });
      // Sync back the merged set (incl. tombstones) so server matches
      syncToServer({ silent: true });
      return state.items;
    }).catch(function (e) {
      console.warn('[mm-wishlist] server load failed', e);
    });
  }

  function syncToServer(opts) {
    opts = opts || {};
    if (!SYNC.enabled || !SYNC.url || !CUSTOMER_ID) return Promise.resolve(null);
    return fetch(SYNC.url, {
      method: 'POST',
      headers: serverHeaders(),
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify({
        customer_id: String(CUSTOMER_ID),
        // Tombstones ride along as pseudo-items so deletions reach other devices
        items: state.items.concat(tombstoneItems()),
        email: state.email || CUSTOMER_EMAIL || null,
      }),
    }).catch(function (e) {
      if (!opts.silent) console.warn('[mm-wishlist] server sync failed', e);
    });
  }

  function scheduleServerSync() {
    if (!SYNC.enabled || !CUSTOMER_ID) return;
    clearTimeout(syncDebounceTimer);
    syncDebounceTimer = setTimeout(function () { syncToServer(); }, 1200);
  }

  // ----- Public API: add / remove / toggle -----
  function add(productData) {
    if (!productData || !productData.productId) return false;
    if (findIndex(productData.productId, productData.variantId) >= 0) return false;
    const item = normalize(productData);
    state.items.push(item);
    write(state.items);
    delete tombstones[itemKey(item.productId, item.variantId)];
    writeTombstones();
    track('mm_wishlist_add', {
      product_id: productData.productId,
      variant_id: productData.variantId,
      product_handle: productData.handle,
      product_title: productData.title,
      source: productData.source || 'unknown',
    });
    emit('added', { productId: String(productData.productId), variantId: String(productData.variantId || ''), items: state.items.slice() });
    trackEvent('Added to Wishlist', item);
    scheduleProfileSync();
    scheduleServerSync();
    return true;
  }

  function remove(productId, variantId) {
    const idx = findIndex(productId, variantId);
    if (idx < 0) return false;
    const removed = state.items.splice(idx, 1)[0];
    write(state.items);
    tombstones[itemKey(productId, variantId)] = Date.now();
    writeTombstones();
    track('mm_wishlist_remove', {
      product_id: productId,
      variant_id: variantId,
      product_handle: removed && removed.handle,
    });
    emit('removed', { productId: String(productId), variantId: String(variantId || ''), items: state.items.slice() });
    trackEvent('Removed from Wishlist', removed);
    scheduleProfileSync();
    scheduleServerSync();
    return true;
  }

  function toggle(productData) {
    if (!productData || !productData.productId) return false;
    if (findIndex(productData.productId, productData.variantId) >= 0) {
      remove(productData.productId, productData.variantId);
      showToast('Quitado de favoritos');
      return false;
    }
    add(productData);
    showToast('Guardado en favoritos');
    return true;
  }

  function isInList(productId, variantId) { return findIndex(productId, variantId) >= 0; }
  function getList() { return state.items.slice(); }
  function getCount() { return state.items.length; }
  function getEmail() { return state.email; }
  function isSubscribed() { return state.subscribed; }

  function clear() {
    const now = Date.now();
    state.items.forEach(function (it) { tombstones[itemKey(it.productId, it.variantId)] = now; });
    writeTombstones();
    state.items = [];
    write(state.items);
    emit('cleared', { items: [] });
    scheduleProfileSync();
    scheduleServerSync();
  }

  function share() {
    // Format: "handle:variantId,handle:variantId" so recipients see the exact variants
    const parts = state.items
      .filter(function (i) { return i.handle; })
      .map(function (i) { return i.handle + (i.variantId ? ':' + i.variantId : ''); });
    const itemsParam = parts.join(',');
    const url = window.location.origin + '/pages/lista-de-favoritos' +
      (itemsParam ? ('?items=' + encodeURIComponent(itemsParam)) : '');
    track('mm_wishlist_share', { count: parts.length });
    return url;
  }

  // Cross-tab sync
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) {
      state.items = read();
      emit('synced', { items: state.items.slice() });
    } else if (e.key === EMAIL_KEY) {
      state.email = readEmail();
    } else if (e.key === SUBSCRIBED_KEY) {
      state.subscribed = readSubscribed();
    } else if (e.key === REMOVED_KEY) {
      // Another tab recorded a removal: adopt it so our next sync does not overwrite it
      tombstones = readTombstones();
    }
  });

  // Header counter sync
  function syncHeaderCounter() {
    const els = document.querySelectorAll('[data-mm-header-wishlist-count]');
    const count = state.items.length;
    els.forEach(function (el) {
      if (count > 0) {
        el.textContent = count > 99 ? '99+' : String(count);
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }
  listeners.push(function (event) {
    if (event === 'ready' || event === 'added' || event === 'removed' || event === 'synced' || event === 'cleared' || event === 'merged') {
      syncHeaderCounter();
    }
  });

  function boot() {
    state.items = read();
    state.email = readEmail() || CUSTOMER_EMAIL || null;
    state.subscribed = readSubscribed();
    // If logged-in customer's email is known, treat as auto-subscribed for profile sync purposes
    if (CUSTOMER_EMAIL && !state.email) state.email = CUSTOMER_EMAIL;
    state.ready = true;
    emit('ready', { items: state.items.slice() });

    // Cross-device load on logged-in users
    if (SYNC.enabled && CUSTOMER_ID) {
      loadFromServer();
    }
  }

  window.MMWishlist = {
    add: add,
    remove: remove,
    toggle: toggle,
    isInList: isInList,
    getList: getList,
    getCount: getCount,
    clear: clear,
    on: on,
    share: share,
    isReady: function () { return state.ready; },
    // Klaviyo + sync
    subscribeEmail: subscribeEmail,
    syncProfile: syncProfile,
    trackEvent: trackEvent,
    getEmail: getEmail,
    isSubscribed: isSubscribed,
    // Server sync
    loadFromServer: loadFromServer,
    syncToServer: syncToServer,
    // Config (read-only)
    config: { klaviyoKey: KLAVIYO_KEY, klaviyoListId: KLAVIYO_LIST_ID, customerId: CUSTOMER_ID, serverSyncEnabled: !!SYNC.enabled },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
